import React, { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import ConfirmModal from '@/components/ConfirmModal'
import { 
  Activity, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Grid3X3,
  Play,
  Pause,
  BarChart3,
  Zap
} from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { botService } from '@/services/bot'
import { useBotStore } from '@/store/bot'

// Componentes auxiliares
const StatCard: React.FC<{
  title: string
  value: string | number
  icon: React.ElementType
  trend?: 'up' | 'down' | 'neutral'
  color?: string
}> = ({ title, value, icon: Icon, trend, color = 'primary' }) => {
  const getIconColor = () => {
    switch (color) {
      case 'success': return 'text-success-600'
      case 'danger': return 'text-danger-600'
      case 'warning': return 'text-warning-600'
      default: return 'text-primary-600'
    }
  }

  const getBgColor = () => {
    switch (color) {
      case 'success': return 'bg-success-50'
      case 'danger': return 'bg-danger-50'
      case 'warning': return 'bg-warning-50'
      default: return 'bg-primary-50'
    }
  }

  return (
    <div className="card">
      <div className="flex items-center">
        <div className={`p-3 rounded-full ${getBgColor()}`}>
          <Icon className={`h-6 w-6 ${getIconColor()}`} />
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <div className="flex items-center">
            <p className="text-2xl font-semibold text-gray-900">{value}</p>
            {trend && (
              <div className="ml-2">
                {trend === 'up' && <TrendingUp className="h-4 w-4 text-success-500" />}
                {trend === 'down' && <TrendingDown className="h-4 w-4 text-danger-500" />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const StatusBadge: React.FC<{ status: boolean; trueText: string; falseText: string }> = ({ 
  status, 
  trueText, 
  falseText 
}) => (
  <span className={`
    inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
    ${status 
      ? 'bg-success-100 text-success-800' 
      : 'bg-gray-100 text-gray-800'
    }
  `}>
    <div className={`
      w-1.5 h-1.5 rounded-full mr-1.5
      ${status ? 'bg-success-400' : 'bg-gray-400'}
    `} />
    {status ? trueText : falseText}
  </span>
)

const DashboardPage: React.FC = () => {
  const { botState, setBotState, stats, setStats, currentPrice, setCurrentPrice } = useBotStore()
  const [actionLoading, setActionLoading] = useState('')
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; action: string }>({ open: false, action: '' })
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  // Queries para obtener datos
  const { data: botStateData, isLoading: botStateLoading } = useQuery(
    'botState',
    botService.getBotState,
    {
      refetchInterval: 5000, // Actualizar cada 5 segundos
      onSuccess: (data) => setBotState(data)
    }
  )

  const { data: statsData } = useQuery(
    'botStats',
    botService.getStats,
    {
      refetchInterval: 10000, // Actualizar cada 10 segundos
      onSuccess: (data) => setStats(data)
    }
  )

  const { data: priceData } = useQuery(
    'currentPrice',
    botService.getCurrentPrice,
    {
      refetchInterval: 2000, // Actualizar cada 2 segundos
      onSuccess: (data) => setCurrentPrice(data.precio)
    }
  )

  const { data: priceHistory = [] } = useQuery(
    'price24h',
    () => botService.getOHLCVData(24, '1h'),
    {
      refetchInterval: 300000, // Actualizar cada 5 minutos
      staleTime: 240000,
      select: (data) => {
        if (data?.datos && Array.isArray(data.datos)) {
          return data.datos.map((item: any) => ({
            timestamp: item.timestamp,
            precio: item.close
          }))
        }
        return []
      }
    }
  )

  if (botStateLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const formatCurrency = (amount: number | null) => {
    if (!amount) return '$0.00'
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
  }

  const handleStartBot = async () => {
    setActionLoading('start')
    try {
      await botService.startBot()
      queryClient.invalidateQueries('botState')
      toast.success('Bot iniciado exitosamente')
    } catch (error) {
      console.error('Error starting bot:', error)
      toast.error('Error al iniciar el bot')
    } finally {
      setActionLoading('')
    }
  }

  const handleStopBot = async () => {
    setConfirmModal({ open: true, action: 'stop' })
  }

  const confirmStopBot = async () => {
    setConfirmModal({ open: false, action: '' })
    setActionLoading('stop')
    try {
      await botService.stopBot()
      queryClient.invalidateQueries('botState')
      toast.success('Bot detenido exitosamente')
    } catch (error) {
      console.error('Error stopping bot:', error)
      toast.error('Error al detener el bot')
    } finally {
      setActionLoading('')
    }
  }

  const handleExecuteCycle = async () => {
    setActionLoading('cycle')
    try {
      await botService.executeCycle()
      queryClient.invalidateQueries('botState')
      toast.success('Ciclo ejecutado exitosamente')
    } catch (error) {
      console.error('Error executing cycle:', error)
      toast.error('Error al ejecutar el ciclo')
    } finally {
      setActionLoading('')
    }
  }

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Monitoreo en tiempo real del Grid Trading Bot</p>
        </div>
        <div className="flex items-center space-x-4">
          <StatusBadge 
            status={botState?.activo || false}
            trueText="Bot Activo"
            falseText="Bot Inactivo"
          />
          <StatusBadge 
            status={botState?.modo_dry_run || false}
            trueText="Modo Simulación"
            falseText="Modo Real"
          />
        </div>
      </div>

      {/* Estadísticas principales */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Capital Total"
          value={formatCurrency(botState?.capital_disponible)}
          icon={DollarSign}
          color="primary"
        />
        <StatCard
          title="Ganancia Total"
          value={formatCurrency(botState?.ganancia_total)}
          icon={TrendingUp}
          color={botState?.ganancia_total && botState.ganancia_total > 0 ? 'success' : 'danger'}
          trend={botState?.ganancia_total && botState.ganancia_total > 0 ? 'up' : 'down'}
        />
        <StatCard
          title="Precio Actual"
          value={currentPrice ? formatCurrency(currentPrice) : 'Cargando...'}
          icon={BarChart3}
          color="warning"
        />
        <StatCard
          title="Posiciones Abiertas"
          value={botState?.posiciones_abiertas || 0}
          icon={Grid3X3}
          color="primary"
        />
      </div>

      {/* Gráfico de Precio BTC 24h */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Precio BTC - Últimas 24 Horas</h3>
          <span className="text-sm text-gray-500">Actualización cada 5 minutos</span>
        </div>
        {priceHistory.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={priceHistory} margin={{ left: 20, right: 10, top: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={(value) => {
                  const date = new Date(value)
                  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
                }}
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                domain={['auto', 'auto']}
                tickFormatter={(value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                width={90}
              />
              <Tooltip 
                formatter={(value: number) => [`$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Precio']}
                labelFormatter={(label) => {
                  const date = new Date(label)
                  return date.toLocaleString('es-ES', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })
                }}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '8px 12px'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="precio" 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[300px]">
            <div className="text-center">
              <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Cargando datos de precio...</p>
            </div>
          </div>
        )}
      </div>

      {/* Información del bot */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Estado del sistema */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Estado del Sistema</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Exchange</span>
              <span className="text-sm font-medium text-gray-900">
                {botState?.exchange || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Par de Trading</span>
              <span className="text-sm font-medium text-gray-900">
                {botState?.par || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Órdenes Pendientes</span>
              <span className="text-sm font-medium text-gray-900">
                {botState?.ordenes_pendientes || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tiempo Activo</span>
              <span className="text-sm font-medium text-gray-900">
                {botState?.uptime || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Rendimiento */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Rendimiento</h3>
          {stats ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Operaciones Totales</span>
                <span className="text-sm font-medium text-gray-900">
                  {stats.rendimiento.operaciones_totales}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Operaciones Ganadoras</span>
                <span className="text-sm font-medium text-gray-900">
                  {stats.rendimiento.operaciones_ganadoras}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Tasa de Éxito</span>
                <span className={`text-sm font-medium ${
                  stats.rendimiento.win_rate > 50 ? 'text-success-600' : 'text-danger-600'
                }`}>
                  {stats.rendimiento.win_rate.toFixed(2)}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Ganancia Acumulada</span>
                <span className={`text-sm font-medium ${
                  stats.rendimiento.ganancia_acumulada > 0 ? 'text-success-600' : 'text-danger-600'
                }`}>
                  {formatCurrency(stats.rendimiento.ganancia_acumulada)}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500">
              <Activity className="h-8 w-8 mx-auto mb-2" />
              <p>Cargando estadísticas...</p>
            </div>
          )}
        </div>
      </div>

      {/* Información del Grid */}
      {stats && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Estado del Grid</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-primary-600">
                {stats.grid.niveles_totales}
              </p>
              <p className="text-sm text-gray-600">Niveles Totales</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-success-600">
                {stats.grid.niveles_ejecutados}
              </p>
              <p className="text-sm text-gray-600">Niveles Ejecutados</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-warning-600">
                {stats.grid.posiciones_abiertas}
              </p>
              <p className="text-sm text-gray-600">Posiciones Abiertas</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-600">
                {stats.grid.ordenes_pendientes}
              </p>
              <p className="text-sm text-gray-600">Órdenes Pendientes</p>
            </div>
          </div>
          
          {/* Barra de progreso */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Progreso del Grid</span>
              <span>
                {stats.grid.niveles_ejecutados} / {stats.grid.niveles_totales}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${(stats.grid.niveles_ejecutados / stats.grid.niveles_totales * 100)}%`
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Acceso rápido */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Acciones Rápidas</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button 
            className="btn-primary disabled:opacity-50" 
            onClick={handleStartBot}
            disabled={actionLoading === 'start' || botState?.activo}
          >
            <Play className="h-4 w-4 mr-2" />
            {actionLoading === 'start' ? 'Iniciando...' : 'Iniciar Bot'}
          </button>
          <button 
            className="btn-secondary disabled:opacity-50"
            onClick={handleStopBot}
            disabled={actionLoading === 'stop' || !botState?.activo}
          >
            <Pause className="h-4 w-4 mr-2" />
            {actionLoading === 'stop' ? 'Deteniendo...' : 'Detener Bot'}
          </button>
          <button 
            className="btn-secondary"
            onClick={() => navigate('/grid')}
          >
            <Grid3X3 className="h-4 w-4 mr-2" />
            Ver Grid
          </button>
          <button 
            className="btn-secondary disabled:opacity-50"
            onClick={handleExecuteCycle}
            disabled={actionLoading === 'cycle' || !botState?.activo}
          >
            <Zap className="h-4 w-4 mr-2" />
            {actionLoading === 'cycle' ? 'Ejecutando...' : 'Ejecutar Ciclo'}
          </button>
        </div>
      </div>
    </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        title="Detener Bot"
        message="¿Estás seguro de que quieres detener el bot?"
        confirmText="Detener"
        variant="danger"
        onConfirm={confirmStopBot}
        onCancel={() => setConfirmModal({ open: false, action: '' })}
      />
    </>
  )
}

export default DashboardPage